import type * as ClassTypes from './types/class';
import {
    initCallbackPopup,
    initForm,
    initInfoPoint,
    initKinescopeVideoPlayer,
    initPlanoplanWidget,
    initPopup,
    initTouchScroll,
    lazyLoadElements
} from './common';
import type {IDynamicImport} from './types/utils';
import {Utils} from './utils';

const header = document.querySelector<HTMLElement>('.j-header');

if (header) {
    import(/* webpackChunkName: "header" */ '@/sections/header')
        .then(({default: Header}: IDynamicImport) => {
            new Header().init({element: header});
        });
}

// Формы
const formWrappers = [...document.querySelectorAll('.j-form')];

formWrappers.forEach((wrapper: Element) => {
    initForm(wrapper as HTMLElement);
});

// Попапы
const popupSelectors = [...document.querySelectorAll('.j-popup')] as HTMLElement[];

if (popupSelectors.length) {
    initPopup(popupSelectors, {removeDelay: 300});
}

// Отдельный селектор для попапов обратных звонков
const popupFacadeLinks = [...document.querySelectorAll('.j-popup-callback:not([data-init])')] as HTMLElement[];

if (popupFacadeLinks.length) {
    initCallbackPopup(popupFacadeLinks);

    popupFacadeLinks.forEach((button: HTMLElement) => {
        button.dataset['init'] = 'true';
    });
}

// Уведомление об использовании cookies
const cookiesWrapper = document.querySelector('.j-cookies-notice') as HTMLElement;

if (cookiesWrapper) {
    import(/* webpackChunkName: "cookies-notice" */ '@/components/cookies-notice')
        .then(({default: CookiesNotice}: IDynamicImport) => {
            const cookiesNotice: ClassTypes.ICookiesNotice = new CookiesNotice();

            cookiesNotice.init(cookiesWrapper);
        });
}

const fsTourWidget = document.querySelector<HTMLElement>('.j-first-screen__widget');

if (fsTourWidget) {
    const uid = fsTourWidget.dataset['uid'];
    const id = fsTourWidget.id;

    if (uid && id) {
        initPlanoplanWidget(uid, 'ru', id);
    }
}

const simpleSliders = [...document.querySelectorAll<HTMLElement>('.j-slider')];

if (simpleSliders.length) {
    import(/* webpackChunkName: "slider" */ '@/components/slider')
        .then(({default: Slider}: IDynamicImport) => {
            simpleSliders.forEach((slider: HTMLElement) => {
                const options = {
                    wrapper: slider,
                    options: {
                        loop      : true,
                        navigation: {
                            nextEl: '.j-slider__next',
                            prevEl: '.j-slider__prev'
                        }
                    }
                };

                new Slider(options).init();
            });
        });
}

const furnitureSlider = document.querySelector<HTMLElement>('.j-furniture-slider');

if (furnitureSlider) {
    import(/* webpackChunkName: "slider" */ '@/components/slider')
        .then(({default: Slider}: IDynamicImport) => {
            const options = {
                wrapper: furnitureSlider,
                options: {
                    loop      : true,
                    speed     : 600,
                    navigation: {
                        nextEl: '.j-furniture-slider__next',
                        prevEl: '.j-furniture-slider__prev'
                    }
                }
            };

            new Slider(options).init();
        });
}

const liveHereSlider = document.querySelector<HTMLElement>('.j-live-here-slider');

if (liveHereSlider) {
    import(/* webpackChunkName: "slider" */ '@/components/slider')
        .then(({default: Slider}: IDynamicImport) => {
            const options = {
                wrapper: liveHereSlider,
                options: {
                    loop       : false,
                    breakpoints: {
                        320: {
                            slidesPerView: 1,
                            spaceBetween : 12
                        },
                        768: {
                            slidesPerView: 2.5,
                            spaceBetween : 18
                        },
                        1280: {
                            slidesPerView: 3,
                            spaceBetween : 24
                        }
                    }
                }
            };

            new Slider(options).init();
        });
}

const plansSliders = [...document.querySelectorAll<HTMLElement>('.j-plans-slider')];

if (plansSliders.length) {
    import(/* webpackChunkName: "slider" */ '@/sections/plans')
        .then(({default: Plans}: IDynamicImport) => {
            plansSliders.forEach((slider: HTMLElement) => {
                new Plans(slider).init();
            });
        });
}

// Details Accordion
const details = [...document.querySelectorAll('.j-details')];

if (details.length) {
    import(/* webpackChunkName: "details-accordion" */ '@/components/details')
        .then(({default: DetailsAccordion}: IDynamicImport) => {
            details.forEach((element: Element) => {
                const accordion: ClassTypes.IDetailsAccordion = new DetailsAccordion();

                accordion.init(element as HTMLDetailsElement);
            });
        });
}

const interactiveCards = [...document.querySelectorAll<HTMLElement>('.j-interactive-cards')];

if (interactiveCards.length) {
    import(/* webpackChunkName: "interactive-card" */ '@/components/interactive-card')
        .then(({default: InteractiveCard}: IDynamicImport) => {
            interactiveCards.forEach((element: HTMLElement) => {
                new InteractiveCard(element).init();
            });
        });
}

const masterPlans = document.querySelector<HTMLElement>('.j-master-plans');

if (masterPlans) {
    import(/* webpackChunkName: "master-plans" */ '@/sections/master-plans')
        .then(({default: MasterPlans}: IDynamicImport) => {
            new MasterPlans({element: masterPlans}).init();
        });
}

const touchScrollElements = [...document.querySelectorAll<HTMLElement>('.j-touch-scroll')];

touchScrollElements.forEach((element: HTMLElement) => {
    initTouchScroll(element);
});

const infoPoints = [...document.querySelectorAll<HTMLElement>('.j-info-point')];

if (infoPoints.length) {
    infoPoints.forEach((point: HTMLElement) => {
        initInfoPoint(point);
    });

    const inner = document.querySelector<HTMLElement>('.j-info-points-inner');
    const content = document.querySelector<HTMLElement>('.j-info-points-content');

    // если мобильное устройство то двигаем на центр
    if (inner && content && Utils.isMobile()) {
        const infoObserver = new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
            if (entries[0]?.isIntersecting) {
                const scrollWidth = content.scrollWidth;
                const clientWidth = inner.offsetWidth;

                setTimeout(() => {
                    inner.scrollTo({
                        left    : (scrollWidth - clientWidth) / 2,
                        behavior: 'smooth'
                    });
                }, 50);

                infoObserver.disconnect();
            }
        });

        infoObserver.observe(inner);

        inner.addEventListener('touchstart', () => {
            if (inner.scrollLeft === 0) {
                inner.scrollLeft = 1;
            }

            if (inner.scrollLeft + inner.offsetWidth >= inner.scrollWidth) {
                inner.scrollLeft = inner.scrollWidth - inner.offsetWidth - 1;
            }
        });
    }
}


/* Ленивая загрузка */
/* подключать после инициализации слайдеров */
const lazyElements = [...document.querySelectorAll('[loading=lazy]')];

if (lazyElements.length) {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            lazyLoadElements(lazyElements);
        });
    } else {
        lazyLoadElements(lazyElements);
    }
}

const videoElements = [...document.querySelectorAll<HTMLVideoElement>('video')];

if (videoElements.length) {
    document.addEventListener('visibilitychange', () => {
        videoElements.forEach((video: HTMLVideoElement) => {
            if (!document.hidden && video.paused) {
                video.play()
                    .then()
                    .catch(() => {
                        console.error('Video play was prevented');
                    });
            }
        });
    });
}

const kinescopeWrappers = [...document.querySelectorAll<HTMLElement>('.j-kinescope')];

kinescopeWrappers.forEach((wrapper: HTMLElement) => {
    initKinescopeVideoPlayer(wrapper);
});

// Добавлено: копирование кода из блоков документации
const copyButtons = [...document.querySelectorAll<HTMLButtonElement>('.j-copy-code')];

if (copyButtons.length) {
    copyButtons.forEach((button: HTMLButtonElement) => {
        button.addEventListener('click', () => {
            const block = button.closest('.code-block');
            const code = block?.querySelector('pre code');
            const text = code?.textContent || '';

            if (!text) {
                return;
            }

            navigator.clipboard?.writeText(text)
                .then(() => {
                    const original = button.textContent || '';

                    button.textContent = 'Скопировано';
                    setTimeout(() => {
                        button.textContent = original || 'Копировать';
                    }, 1500);
                })
                .catch(() => {
                    try {
                        const textarea = document.createElement('textarea');

                        textarea.value = text;
                        textarea.style.position = 'fixed';
                        textarea.style.top = '0';
                        textarea.style.left = '0';
                        textarea.style.width = '1px';
                        textarea.style.height = '1px';
                        textarea.style.opacity = '0';
                        document.body.appendChild(textarea);
                        textarea.focus();
                        textarea.select();
                        document.execCommand('copy');
                        document.body.removeChild(textarea);
                        const original = button.textContent || '';

                        button.textContent = 'Скопировано';
                        setTimeout(() => {
                            button.textContent = original || 'Копировать';
                        }, 1500);
                    } catch (error) {
                        console.error('Copy failed', error);
                    }
                });
        });
    });
}
